import React from "react";
import { Alert, Box, Button } from "@mui/material";
import { FONT_SIZES } from "../constants/fontSizes";
import QueryBuilderIcon from "@mui/icons-material/QueryBuilder";
import DirectionsRunIcon from "@mui/icons-material/DirectionsRun";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import CalculateIcon from "@mui/icons-material/Calculate";
import SummarizeIcon from "@mui/icons-material/Summarize";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SecurityIcon from "@mui/icons-material/Security";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import CloseIcon from "@mui/icons-material/Close";
import { styles as inputFormStyles } from "./user_input/UserInputForm.styles";
import colors from "../constants/colors";

const StatusDisplay = ({
  status,
  error,
  setShowTutorial,
  onCancelJob,
  jobId,
}) => {
  if (error) {
    console.log("error", error, status);
    if (error.includes("Insufficient credits")) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="100%"
        >
          <Alert
            severity="error"
            sx={{ mb: 2, width: "100%", textAlign: "center" }}
          >
            You do not have enough credits to run this job. Please purchase more
            credits.
          </Alert>
        </Box>
      );
    }
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        width="100%"
      >
        <Alert
          severity="error"
          sx={{ mb: 2, width: "100%", textAlign: "center" }}
        >
          There was an error in job execution, an error report has been sent to
          the developers. Please try again later.
        </Alert>
      </Box>
    );
  }

  if (!status) return null;

  const severityMap = {
    pending: "warning",
    running: "info",
    "Generating Name Candidates": "info",
    "Searching Trademarks": "info",
    "Searching Domains": "info",
    "Scoring Matches to Trademarks": "info",
    "Scoring Domain Availability": "info",
    "Scoring Brand Names": "info",
    "Compiling Detailed Report": "info",
    "Compiling Report Summary": "info",
    "Prompt Insecure": "warning",
    "Checking Security": "info",
    completed: "success",
    failed: "error",
    cancelled: "info",
  };

  const iconMap = {
    pending: <QueryBuilderIcon />,
    running: <DirectionsRunIcon />,
    "Generating Name Candidates": <DirectionsRunIcon />,
    "Searching Trademarks": <ManageSearchIcon />,
    "Searching Domains": <ManageSearchIcon />,
    "Scoring Matches to Trademarks": <CalculateIcon />,
    "Scoring Domain Availability": <CalculateIcon />,
    "Scoring Brand Names": <CalculateIcon />,
    "Compiling Detailed Report": <SummarizeIcon />,
    "Compiling Report Summary": <SummarizeIcon />,
    "Prompt Insecure": <SecurityIcon />,
    "Checking Security": <SecurityIcon />,
    completed: <CheckCircleIcon />,
    failed: <ErrorOutlineIcon />,
    cancelled: <CloseIcon />,
  };

  const messageMap = {
    pending: "Job is pending...",
    running: "Processing trademark check...",
    "Prompt Insecure": (
      <>
        User input contains insecure content. Please follow instructions from
        the{" "}
        <Button
          startIcon={<MenuBookIcon />}
          onClick={() => setShowTutorial(true)}
          sx={{ textTransform: "none", p: 0, minWidth: "auto", ml: 1 }}
        >
          documentation
        </Button>{" "}
        and update your inputs.
      </>
    ),
    completed: "Job completed successfully!",
    failed: "Job failed. Please try again.",
    cancelled: "Job was cancelled.",
  };

  const showCancelButton =
    onCancelJob &&
    jobId &&
    status !== "completed" &&
    status !== "failed" &&
    status !== "cancelled";

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="100%"
    >
      <Alert
        severity={severityMap[status] || "info"}
        icon={iconMap[status]}
        sx={{
          mb: 2,
          width: "100%",
          textAlign: "center",
          "& .MuiAlert-message": {
            fontSize: FONT_SIZES.PARAGRAPH,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
          },
          "& .MuiAlert-icon": {
            fontSize: "1.5rem",
            alignItems: "center",
          },
        }}
        action={
          showCancelButton ? (
            <Button
              variant="contained"
              color="primary"
              sx={{
                ...inputFormStyles.submitButton,
                ml: 1,
                boxShadow: "none",
                backgroundColor: colors.sienna[700],
                "&:hover": {
                  backgroundColor: colors.sienna[800],
                },
                fontSize: FONT_SIZES.BUTTON_TEXT_MEDIUM,
              }}
              onClick={onCancelJob}
            >
              Cancel Job
            </Button>
          ) : null
        }
      >
        {messageMap[status] || status}
      </Alert>
    </Box>
  );
};

export default StatusDisplay;
